import { useEffect, useRef, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { userProgressKeys } from "./useUserProgress";
import { useToast } from "./use-toast";

const LOCAL_HISTORY_KEY = "quiz_local_history";
const SYNC_STATUS_KEY = "quiz_sync_status";

interface LocalHistoryEntry {
  taskId: string;
  isCorrect: boolean;
  timestamp: number;
}

interface SyncStatus {
  lastSyncAt: number;
  userId: string | null;
}

// Save history entry to local storage (for non-logged users or as backup)
export function saveToLocalHistory(taskId: string, isCorrect: boolean) {
  try {
    const existing = getLocalHistory();
    
    // Check if task already exists - update if so
    const existingIndex = existing.findIndex(e => e.taskId === taskId);
    if (existingIndex >= 0) {
      existing[existingIndex] = { taskId, isCorrect, timestamp: Date.now() };
    } else {
      existing.push({ taskId, isCorrect, timestamp: Date.now() });
    }
    
    // Keep last 500 entries
    const trimmed = existing.slice(-500);
    localStorage.setItem(LOCAL_HISTORY_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error("Error saving to local history:", error);
  }
}

// Get local history
export function getLocalHistory(): LocalHistoryEntry[] {
  try {
    const stored = localStorage.getItem(LOCAL_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Clear local history after successful sync
export function clearLocalHistory() {
  try {
    localStorage.removeItem(LOCAL_HISTORY_KEY);
  } catch (error) {
    console.error("Error clearing local history:", error);
  }
}

// Get sync status
function getSyncStatus(): SyncStatus | null {
  try {
    const stored = localStorage.getItem(SYNC_STATUS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

// Save sync status
function saveSyncStatus(userId: string) {
  try {
    const status: SyncStatus = {
      lastSyncAt: Date.now(),
      userId,
    };
    localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(status));
  } catch (error) {
    console.error("Error saving sync status:", error);
  }
}

// Hook to handle progress synchronization on login
export function useProgressSync() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const hasSynced = useRef(false);

  const syncMutation = useMutation({
    mutationFn: async () => {
      if (!user) return { synced: 0 };

      const localHistory = getLocalHistory();
      if (localHistory.length === 0) {
        return { synced: 0 };
      }

      // Get existing progress from database to avoid re-syncing
      const { data: existingProgress } = await supabase
        .from("user_progress")
        .select("task_id")
        .eq("user_id", user.id);

      const existingTaskIds = new Set(existingProgress?.map(p => p.task_id) || []);
      
      // Filter out entries that already exist in database
      const toSync = localHistory.filter(entry => !existingTaskIds.has(entry.taskId));
      
      if (toSync.length === 0) {
        return { synced: 0, alreadySynced: localHistory.length };
      }

      // Batch sync - process in chunks to avoid timeout
      const CHUNK_SIZE = 50;
      let syncedCount = 0;
      
      for (let i = 0; i < toSync.length; i += CHUNK_SIZE) {
        const chunk = toSync.slice(i, i + CHUNK_SIZE);
        
        for (const entry of chunk) {
          try {
            await supabase.rpc("record_task_attempt", {
              p_task_id: entry.taskId,
              p_is_correct: entry.isCorrect,
            });
            syncedCount++;
          } catch (error) {
            console.error("Error syncing entry:", entry.taskId, error);
            // Continue with other entries
          }
        }
      }

      return { synced: syncedCount, total: toSync.length };
    },
    onSuccess: (result) => {
      if (result && result.synced > 0 && user) {
        // Clear local history after successful sync
        clearLocalHistory();
        saveSyncStatus(user.id);
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: userProgressKeys.all });
        
        toast({
          title: "Postęp zsynchronizowany",
          description: `Zsynchronizowano ${result.synced} ${result.synced === 1 ? 'odpowiedź' : 'odpowiedzi'} z bazą danych.`,
        });
      }
    },
    onError: (error) => {
      console.error("Sync failed:", error);
      toast({
        title: "Błąd synchronizacji",
        description: "Nie udało się zsynchronizować postępu. Spróbuj ponownie później.",
        variant: "destructive",
      });
    },
  });

  // Reset sync flag when user changes (logout → login)
  useEffect(() => {
    hasSynced.current = false;
  }, [user?.id]);

  // Sync on login
  useEffect(() => {
    if (authLoading || hasSynced.current || !user) return;

    const localHistory = getLocalHistory();
    if (localHistory.length > 0) {
      hasSynced.current = true;
      syncMutation.mutate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Manual sync function
  const manualSync = useCallback(() => {
    if (user && !syncMutation.isPending) {
      syncMutation.mutate();
    }
  }, [user, syncMutation]);

  return {
    isSyncing: syncMutation.isPending,
    manualSync,
    localHistoryCount: getLocalHistory().length,
  };
}

// Hook to load progress from database on login
export function useLoadProgressFromDatabase(
  onProgressLoaded: (progress: Array<{ taskId: string; isCorrect: boolean }>) => void
) {
  const { user, isLoading: authLoading } = useAuth();
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (authLoading || hasLoaded.current || !user) return;

    const loadProgress = async () => {
      hasLoaded.current = true;
      
      try {
        // Load both user_progress and open_task_responses in parallel
        const [progressResult, openTasksResult] = await Promise.all([
          supabase
            .from("user_progress")
            .select("task_id, is_correct")
            .eq("user_id", user.id),
          supabase
            .from("open_task_responses")
            .select("task_id, verdict_answer")
            .eq("user_id", user.id)
        ]);

        if (progressResult.error) {
          console.error("Error loading progress:", progressResult.error);
        }
        
        if (openTasksResult.error) {
          console.error("Error loading open task responses:", openTasksResult.error);
        }

        // Combine progress from both sources
        const progressMap = new Map<string, boolean>();
        
        // Add regular progress
        if (progressResult.data) {
          progressResult.data.forEach(p => {
            progressMap.set(p.task_id, p.is_correct);
          });
        }
        
        // Add open task responses — but NOT those with wrong verdicts
        // (verdict_answer is null for tasks without verdict options = always count as completed)
        if (openTasksResult.data) {
          openTasksResult.data.forEach(r => {
            // Skip tasks that have a verdict_answer set — we can't check correctness here
            // without knowing the task's correctIndex, so we need to fetch tasks too.
            // For simplicity: if verdict_answer is null, treat as completed (no verdict task).
            // If verdict_answer is set, don't auto-mark as completed — let the actual
            // grading determine this (wrong verdict = grade "incorrect" in DB).
            if (!progressMap.has(r.task_id) && r.verdict_answer === null) {
              progressMap.set(r.task_id, true);
            }
          });
        }

        if (progressMap.size > 0) {
          const progress = Array.from(progressMap.entries()).map(([taskId, isCorrect]) => ({
            taskId,
            isCorrect,
          }));
          onProgressLoaded(progress);
        }
      } catch (error) {
        console.error("Error loading progress:", error);
      }
    };

    loadProgress();
  }, [user, authLoading, onProgressLoaded]);
}
